import { getEnv, types, flow } from "mobx-state-tree";
import { reaction } from "mobx";

import {
  UserDetailsResponse,
  CurrentOrganizationResponse,
} from "../../api-models/response/UserDetailsResponse";
import { permissionsService } from "../authorization/UserPermissionsService";
import { StorageModel } from "../storage/StorageModel";
import { appSessionStorage } from "../storage/SessionStorageService";
import { api } from "../api/ApiService";
import { globalModule } from "../service-provider/GlobalModule";
import { organizationSubscriptionService } from "../organization/OrganizationSubscription";
import { endpoints } from "../api/ApiEndpoints";
import { OrganizationKey } from "../organization/LastAccessedOrganizationStorage";
import { AccessOrganization } from "../authorization/Permissions";
import { UserRoles } from "../../generated/Users";

export const noUserData: UserDetailsResponse = {
  id: 0,
  firstName: "",
  lastName: "",
  userName: "",
  name: "",
  employmentStatus: {
    id: 0,
    name: "",
    isDefault: false,
  },
  role: {
    id: UserRoles.Initial,
    name: "",
    default: false,
  },
  status: {
    id: 0,
    name: "",
    isDefault: false,
  },
  email: "",
  phone: "",
  currentOrganization: {
    id: 0,
    name: "",
    isDeleted: false,
    hasVat: false,
    country: "",
    hasPaidSubscription: false,
    isDemo: true,
    allowEnterprise: false,
  },
  comments: "",
  created: new Date(),
  createdBy: {
    id: 0,
    name: "",
    isDeleted: false,
  },
  userOrganizations: [],
  registrationDate: new Date(),
  invitedToOrganizationBy: {
    id: 0,
    name: "",
    isDeleted: false,
  },
  invitedToOrganizationDate: new Date(),
  lockedInspectionsCount: 0,
  isSupportAccount: false,
  creditBalance: 0,
  isDemo: false
};

const CurrentUserModel = types.model({
  currentUser: types.optional(types.frozen<UserDetailsResponse>(), noUserData),
});

export const CurrentUserStorageModel = types
  .compose(CurrentUserModel, StorageModel)
  .named("CurrentUserStorrageModel")
  .actions((self) => {
    const updateCurrentUserDetails = (organizationId: any) => {
      const organization = self.currentUser.userOrganizations?.find(
        (x) => x.id === Number(organizationId)
      ) as CurrentOrganizationResponse;
      let adjustedCurrentUserDetails: UserDetailsResponse;
      adjustedCurrentUserDetails = { ...self.currentUser, currentOrganization: organization };
      self.currentUser = adjustedCurrentUserDetails;
    };

    const changeOrganization = (organizationId: any) => {
      updateCurrentUserDetails(organizationId);
    };

    const loadInitialOrganization = () => {
      const storedOragnizationId = appSessionStorage.getStorageItem(OrganizationKey);
      const organizationWithStoredId = self.currentUser.userOrganizations?.find(
        (c) => c.id === storedOragnizationId
      );

      if (!organizationWithStoredId) return;

      if (!storedOragnizationId || organizationWithStoredId === undefined) {
        changeOrganization(self.currentUser.currentOrganization.id);
      } else {
        changeOrganization(storedOragnizationId);
      }
    };

    return {
      mapResponse(response: UserDetailsResponse) {
        self.currentUser = response || noUserData;

        loadInitialOrganization();
      },

      setCurrentOrganization(organizationId: any) {
        changeOrganization(organizationId);
      },

      closeUserAccount: flow(function* () {
        const endpoint = endpoints.get("close_user_account");
        yield getEnv(self).api.delete(endpoint);
      }),
    };
  })
  .views((self) => ({
    get userIsLoaded() {
      return !!self.currentUser.id;
    },

    get endpoint() {
      const endpoint = endpoints.get("users_current");
      return endpoint;
    },
  }))
  .views((self) => ({
    get isLoading() {
      return !self.userIsLoaded;
    },
    get canLoadItems() {
      return getEnv(self).permissionsService.hasPermissions;
    },
    get userIsPartOfAnOrganization() {
      return getEnv(self).permissionsService.userHasPermissionTo(AccessOrganization);
    },
    get hasCurrentOrganizationVatWarning() {
      return (
        !self.currentUser.currentOrganization.hasVat &&
        self.currentUser.currentOrganization.country !== "DE"
      );
    },
  }));

export const currentUserService = CurrentUserStorageModel.create(
  {},
  {
    appSessionStorage,
    api,
    getPath,
    permissionsService,
    organizationSubscriptionService,
  }
);
globalModule.currentUserService = currentUserService;

function getPath() {
  return window.location.pathname;
}

reaction(
  () => permissionsService.hasPermissions,
  async () => {
    await currentUserService.load();
  }
);
